/**
 * The four fees, in a minute — the primer at the top of step 1 (UX review, 17 Oct 2026).
 *
 * THE GUIDE IS A LINK, NOT A GATE. "Understanding Aged Care Costs" used to stand in front of every
 * route into the module, including a saved plan. It is good content in the way. So: the first time a
 * client reaches step 1 the primer is open — the room, the basic daily fee, the two means-tested
 * contributions, extras — and after that it collapses to one line they can reopen any time; the full
 * guide stays one click away from every step; every figure that uses a term explains it in the same
 * sentence; and "what's this?" goes to the glossary.
 *
 * THE RULE FOR TERMS (owner, 17 Oct 2026): plain words first, the full name once, the acronym in
 * brackets, and the sentence must make sense with the bracket removed. RAD and DAP are kept, in bold,
 * because the home's quote and the Services Australia letter say them and they recur on every screen;
 * HSC and NCCC follow the everyday names with their full names (owner, 22 Oct 2026).
 *
 * ADAPTS TO THE CLIENT'S RULE SET: a resident on the 1 July 2014 arrangements (the no-worse-off
 * principle) is never shown a fee that is not on their own fee schedule.
 */
import React, { useState } from 'react';
import { BookOpen } from 'lucide-react';
import { RATES_PROVENANCE } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { AGED_CARE_SCHEDULE } from '../../../../shared/src/calculation/constants/agedCareSchedule.ts';
import { formatCurrency } from '../../utils/index.ts';
import { PrimerTitle, PRIMER_RAIL, PRIMER_LEAD, PRIMER_FIGURE, PRIMER_TOGGLE, primerToggleWord } from '../../components/strategy/PrimerHeader.tsx';

// The fee in force, to the cent, from the rates file — it moves when the rates are updated (owner, 22 Oct 2026).
const dailyFee = `$${AGED_CARE_SCHEDULE.basicDailyFeeDaily.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SEEN_KEY = 'acw.agedCareFeesPrimerSeen';

interface AgedCareFeesPrimerProps {
    /** True for a resident on the 1 July 2014 fee arrangements (means-tested care fee, with caps). */
    oldRules?: boolean;
    /** Opens the full guide, keeping everything on the page as it is. */
    onReadGuide: () => void;
}

const AgedCareFeesPrimer = ({ oldRules = false, onReadGuide }: AgedCareFeesPrimerProps) => {
    const [open, setOpen] = useState<boolean>(() => {
        try { return sessionStorage.getItem(SEEN_KEY) !== '1'; } catch { return true; }
    });
    const toggle = () => {
        setOpen((v) => !v);
        try { sessionStorage.setItem(SEEN_KEY, '1'); } catch { /* storage unavailable: the primer simply opens again next time */ }
    };
    return (
        <details open={open} className={`group rounded-2xl border border-slate-200 ${PRIMER_RAIL} bg-white shadow-card`} data-testid="fees-primer" onToggle={(e) => { if ((e.target as HTMLDetailsElement).open !== open) toggle(); }}>
            <summary className="flex cursor-pointer list-none items-center gap-3 px-5 py-3.5 [&::-webkit-details-marker]:hidden">
                <PrimerTitle as="span">{open ? 'New to aged care fees? The four fees, in a minute' : 'How the fees work — the four fees, in a minute'}</PrimerTitle>
                <span className={`ml-auto ${PRIMER_TOGGLE}`}>{primerToggleWord(open)}</span>
            </summary>
            <div className="grid gap-2.5 px-5 pb-4">
                <Line term="The room">
                    Every home sets a price for its room. You can pay it up front as a refundable deposit — a <strong>Refundable Accommodation Deposit (RAD)</strong>, which comes back when care ends — or pay it daily, like rent — a <strong>Daily Accommodation Payment (DAP)</strong>, which never comes back — or a mix. Step 3 is where you choose how much of each.
                </Line>
                <Line term="The basic daily fee">
                    Everyone pays it. It covers meals, cleaning, laundry and the like, and is set by government at {Math.round(AGED_CARE_SCHEDULE.basicDailyFeeRate * 100)}% of the single Age Pension. It is currently <strong className={PRIMER_FIGURE}>{dailyFee} a day</strong> (about {formatCurrency(AGED_CARE_SCHEDULE.basicDailyFeeDaily * 365)} a year).
                </Line>
                {oldRules ? (
                    <Line term="A means-tested care fee">
                        Because a home care package was in place on 12 September 2024, the &quot;no worse off&quot; rule keeps this person on the 1 July 2014 arrangements: one means-tested care fee, capped each year and over a lifetime, instead of the two contributions newer residents pay. We work it out for you.
                    </Line>
                ) : (
                    <Line term="Two means-tested contributions">
                        An <strong>everyday living contribution</strong> — on the fee schedule, the Hotelling Supplement Contribution (HSC) — and a <strong>care contribution</strong> — the Non-Clinical Care Contribution (NCCC). Whether they are paid, and how much, depends on income and assets; both are capped, and we work them out for you.
                    </Line>
                )}
                <Line term="Extras">
                    Some homes charge for optional extras — a bigger room, a wine list. Only if you choose them.
                </Line>
                <p className="mt-1 text-[13px] text-slate-500">
                    You don&apos;t need to understand how any of this is calculated; we do that, with the rates in force from {RATES_PROVENANCE.agePension}.{' '}
                    <button type="button" onClick={onReadGuide} className="inline-flex items-center gap-1 font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">
                        <BookOpen size={14} /> Read the full guide to aged care fees
                    </button>{' '}
                    if you want the detail.
                </p>
            </div>
        </details>
    );
};

const Line = ({ term, children }: { term: string; children: React.ReactNode }) => (
    <div className="grid grid-cols-1 gap-1 border-t border-slate-100 pt-2.5 text-[14px] leading-relaxed text-slate-700 md:grid-cols-[200px_minmax(0,1fr)] md:gap-4">
        <span className={`font-bold ${PRIMER_LEAD}`}>{term}</span>
        <span>{children}</span>
    </div>
);

export default AgedCareFeesPrimer;
